import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Database,
    CheckCircle,
    RefreshCw,
    ExternalLink,
    ArrowLeft,
    Clock,
    AlertCircle,
    XCircle,
    AlertTriangle,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotionIntegration } from '@/hooks/useNotionIntegration';
import { completeNotionIntegration, getNotionExportHistory, NotionExportHistoryItem, fetchSubscription, type Subscription } from '@/services/api';
import { cn } from '@/lib/utils';
import toast from 'react-hot-toast';
import { ControlledToggleButton } from '@/components/ControlledToggleButton';
import { NotionDisconnectDialog } from '@/components/NotionDisconnectDialog';

export const NotionStatusPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isProcessingCallback, setIsProcessingCallback] = useState(false);
    const [syncHistory, setSyncHistory] = useState<NotionExportHistoryItem[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

    const {
        status,
        isLoading,
        isConnecting,
        isDisconnecting,
        isSyncing,
        toggleAutoSync,
        manualSync,
        disconnect,
        refreshStatus,
        formatDate,
        isConnected
    } = useNotionIntegration();

    // Check if user is on free plan
    const [isFreePlan, setIsFreePlan] = useState<boolean>(false);

    useEffect(() => {
        console.log('NotionStatusPage - isFreePlan changed to:', isFreePlan);
    }, [isFreePlan]);

    // Fetch subscription data
    useEffect(() => {
        const loadSubscription = async () => {
            try {
                const subscriptionData = await fetchSubscription();
                setSubscription(subscriptionData);
            } catch (error) {
                setSubscription(null);
            }
        };

        loadSubscription();
    }, []);

    // Determine if user is on free plan
    useEffect(() => {
        const checkFreePlan = () => {
            const activeSubscriptionStatuses = ['active', 'trialing'];
            const hasActiveSubscription = subscription && activeSubscriptionStatuses.includes(subscription.status);
            const premiumUntil = localStorage.getItem('premiumUntil');
            
            // Check if premiumUntil is valid and not expired
            const isPremiumUntilValid = premiumUntil && 
                premiumUntil !== 'null' && 
                premiumUntil !== 'undefined' &&
                new Date(premiumUntil) > new Date();
            
            const isPremium = hasActiveSubscription && isPremiumUntilValid;
            
            setIsFreePlan(!isPremium);
        };

        checkFreePlan();
    }, [subscription]);

    // Fetch sync history
    const fetchSyncHistory = async () => {
        try {
            setIsLoadingHistory(true);
            const response = await getNotionExportHistory(1, 5);
            setSyncHistory(response.exports);
        } catch (error) {
            console.error('Failed to fetch sync history:', error);
            toast.error('Failed to load sync history');
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // Handle callback from Notion OAuth
    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (code && state) {
            handleNotionCallback(code, state);
        }
    }, [searchParams]);

    const handleNotionCallback = async (code: string, state: string) => {
        try {
            setIsProcessingCallback(true);

            // Verify state parameter
            const storedState = localStorage.getItem('notion_auth_state');
            if (state !== storedState) {
                console.error('Invalid authorization state. Please try again.');
                throw new Error('Invalid authorization state. Please try again.');
            }

            const redirectUri = `${window.location.origin}/notion/status`;
            await completeNotionIntegration(code, state, redirectUri);

            // Clear stored state
            localStorage.removeItem('notion_auth_state');

            toast.success('Notion integration completed successfully!');

            // Refresh status
            await refreshStatus();

            // Redirect to clean status page (without query params)
            navigate('/notion/status', { replace: true });

        } catch (error) {
            console.error('Failed to complete Notion integration:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to complete integration. Please try again.';
            toast.error(errorMessage);

            // Redirect to integrations page on error
            navigate('/profile?active=integrations');
        } finally {
            setIsProcessingCallback(false);
        }
    };

    // Redirect to integrations if not connected and not processing callback
    React.useEffect(() => {
        if (!isLoading && !isConnected && !isProcessingCallback) {
            navigate('/profile?active=integrations');
        }
    }, [isLoading, isConnected, navigate, isProcessingCallback]);

    // Fetch sync history when component mounts
    React.useEffect(() => {
        if (isConnected && !isLoading) {
            fetchSyncHistory();
        }
    }, [isConnected, isLoading]);

    // Get status icon and color for sync history items
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'COMPLETED':
                return <CheckCircle className="h-4 w-4 text-green-500" />;
            case 'PROCESSING':
                return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
            case 'FAILED':
                return <XCircle className="h-4 w-4 text-red-500" />;
            default:
                return <AlertCircle className="h-4 w-4 text-gray-500" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'COMPLETED':
                return 'text-green-800 bg-green-200 border-green-200';
            case 'PROCESSING':
                return 'text-blue-600 bg-blue-50 border-blue-200';
            case 'FAILED':
                return 'text-red-800 bg-red-200 border-red-200';
            default:
                return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    if (isLoading || isProcessingCallback) {
        return (
            <div className="min-h-screen bg-gray-900 py-8">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-900 rounded-full mb-4">
                            <RefreshCw className="h-8 w-8 text-blue-400 animate-spin" />
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">
                            {isProcessingCallback ? 'Completing Integration...' : 'Loading...'}
                        </h1>
                        <p className="text-gray-400">
                            {isProcessingCallback
                                ? 'Please wait while we complete your Notion integration.'
                                : 'Loading integration status...'
                            }
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!isConnected || !status) {
        return null; // Will redirect to setup
    }

    return (
        <div className="min-h-screen bg-gray-900 py-8">
            <div className="max-w-4xl mx-auto px-4">
                {/* Header */}
                <div className="mb-8">
                    <div className="mb-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/profile?active=integrations')}
                            className="text-gray-400 bg-transparent hover:bg-transparent hover:text-gray-400"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Integrations
                        </Button>
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-white mb-2">
                                Notion Integration Status
                            </h1>
                            <p className="text-gray-400">
                                Manage your Notion workspace connection and sync settings.
                            </p>
                        </div>

                        <Badge variant="outline" className="text-green-600 border-green-200">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Active
                        </Badge>
                    </div>
                </div>

                <div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Main Status Card */}
                        <div className="lg:col-span-3">
                            <Card className="mb-6 bg-gray-800/50 border-gray-700">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-white">
                                        <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                                            <img
                                                src="/assets/notion.svg"
                                                alt="Notion Logo"
                                                className="text-gray-400"
                                            />
                                        </div>
                                        Integration Overview
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Connection Status */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="h-5 w-5 text-green-500" />
                                            <span className="font-medium text-white">Connected</span>
                                        </div>
                                        <div className="text-sm text-gray-400">
                                            Last sync: {formatDate(status.last_sync_at)}
                                        </div>
                                    </div>

                                    {/* Auto-Sync Settings */}
                                    <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="font-medium text-white">🔄 Auto-Sync Settings</h4>
                                            <ControlledToggleButton
                                                isOn={status.auto_sync_enabled}
                                                onToggle={toggleAutoSync}
                                                disabled={isConnecting}
                                            />
                                        </div>
                                        <p className="text-sm text-gray-400">
                                            {status.auto_sync_enabled
                                                ? 'New posts are automatically synced to your Notion database.'
                                                : 'Auto-sync is disabled. Enable to sync new posts automatically.'
                                            }
                                        </p>

                                        {/* Free plan callout */}
                                        {(() => {
                                            return isFreePlan ? (
                                                <div className="mt-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-3">
                                                    <div className="flex items-start gap-2">
                                                        <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 my-auto" />
                                                        <p className="text-sm text-yellow-300">
                                                            Since you are on a free plan, only <span className="font-semibold">10 posts</span> will get synced to Notion as a sample.{' '}
                                                            <a
                                                                onClick={() => navigate('/profile?active=subscription')}
                                                                className="underline bg-transparent hover:bg-transparent hover:text-yellow-200 transition-colors cursor-pointer"
                                                            >
                                                                Purchase subscription
                                                            </a> to sync all posts.
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <Button
                                            variant="outline"
                                            onClick={async () => {
                                                await manualSync();
                                                // Refresh sync history after manual sync
                                                setTimeout(() => {
                                                    fetchSyncHistory();
                                                    document.getElementById('sync-history')?.scrollIntoView({ behavior: 'smooth' });
                                                }, 1000);
                                            }}
                                            disabled={isSyncing}
                                            className="w-full bg-gray-700 border-gray-600 text-white hover:text-white hover:bg-gray-600"
                                        >
                                            {isSyncing ? (
                                                <>
                                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                    Syncing...
                                                </>
                                            ) : (
                                                <>
                                                    <RefreshCw className="h-4 w-4 mr-2" />
                                                    Sync Now
                                                </>
                                            )}
                                        </Button>

                                        {status.database_url && (
                                            <Button
                                                variant="outline"
                                                onClick={() => window.open(status.database_url!, '_blank')}
                                                className="w-full bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                                            >
                                                <ExternalLink className="h-4 w-4 mr-2" />
                                                View in Notion
                                            </Button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-1 ">

                                        <Button
                                            variant="outline"
                                            onClick={() => setShowDisconnectDialog(true)}
                                            disabled={isDisconnecting}
                                            className="w-full bg-gray-700 border-gray-600 text-red-400 hover:text-red-300 hover:bg-gray-600"
                                            size="sm"
                                        >
                                            {isDisconnecting ? (
                                                <>
                                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                    Disconnecting...
                                                </>
                                            ) : (
                                                <>
                                                    <Database className="h-4 w-4 mr-2" />
                                                    Disconnect
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* Sync History */}
                    <Card id='sync-history' className="bg-gray-800/50 border-gray-700">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center justify-between text-white">
                                <span>Sync History</span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {isLoadingHistory ? (
                                <div className="text-center py-4">
                                    <RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-400">Loading sync history...</p>
                                </div>
                            ) : syncHistory.length === 0 ? (
                                <div className="text-center py-4">
                                    <Clock className="h-6 w-6 mx-auto text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-400">No sync history yet</p>
                                </div>
                            ) : (
                                syncHistory.map((sync) => (
                                    <div key={sync.export_id} className="border border-gray-700 rounded-lg p-4 bg-gray-900/30">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                {getStatusIcon(sync.status)}
                                                <span className="text-sm font-medium text-white">
                                                    {sync.export_type === 'manual' ? 'Manual Sync' : 'Auto Sync'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col items-end gap-2">
                                                <Badge className={cn("text-xs", getStatusColor(sync.status))}>
                                                    {sync.status}
                                                </Badge>
                                                <span className="text-xs text-gray-400">
                                                    {(() => {
                                                        const utcString = sync.created_at.endsWith('Z') ? sync.created_at : sync.created_at + 'Z';
                                                        const d = new Date(utcString);
                                                        const options: Intl.DateTimeFormatOptions = {
                                                            day: '2-digit',
                                                            month: 'short',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit',
                                                            hour12: true,
                                                        };
                                                        return d.toLocaleString(undefined, options);
                                                    })()}
                                                </span>
                                            </div>
                                        </div>

                                        {sync.error_message && (
                                            <div className="mt-3 text-xs text-red-400 bg-red-900/20 border border-red-800/50 p-2 rounded">
                                                <strong>Error:</strong> {sync.error_message}
                                            </div>
                                        )}
                                    </div>
                                ))
                    )}
                </CardContent>
            </Card>

            <NotionDisconnectDialog
                isOpen={showDisconnectDialog}
                onClose={() => setShowDisconnectDialog(false)}
                onConfirm={disconnect}
            />
                </div>
            </div>
        </div>
    );
};
